<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import type { MenuItem } from 'primevue/menuitem';
import TieredMenu, { type TieredMenuPassThroughOptions } from 'primevue/tieredmenu';
import ListNarrowIcon from '@peanuds/ui/assets/icons/list-narrow.svg?component';
import { authRouteNames, features, useFeatureFlag } from '@peanuds/app-shell';
import { DATA_EXPORT_ROUTE_NAME } from '@peanuds/data-export';
import { type AccountSwitcherItem, useAccountSwitcher } from './useAccountSwitcher';

defineOptions({ name: 'AppAccountSwitcher' });

const emit = defineEmits<{ close: [] }>();

const VISIBLE_ACCOUNT_LIMIT = 3;
const ACCOUNT_MENU_TRIGGER_KEY = 'account-switcher-more';
const ACCOUNT_MENU_HEADER_KEY = 'account-switcher-all-accounts';
const ACCOUNT_MENU_FOOTER_SEPARATOR_KEY = 'account-switcher-footer-separator';

interface AccountMenuItem extends MenuItem {
  account: AccountSwitcherItem;
  key: string;
  label: string;
}

const accountMenuPassThrough = {
  root: { class: 'app-account-switcher__menu' },
  rootList: { class: 'app-account-switcher__menu-list app-account-switcher__menu-list--root' },
  item: { class: 'app-account-switcher__menu-item' },
  itemContent: { class: 'app-account-switcher__menu-item-content' },
  itemLink: { class: 'app-account-switcher__menu-action' },
  itemIcon: { class: 'app-account-switcher__menu-icon' },
  itemLabel: { class: 'app-account-switcher__menu-label' },
  separator: { class: 'app-account-switcher__menu-separator' },
  submenu: { class: 'app-account-switcher__menu-list app-account-switcher__menu-list--submenu' },
} satisfies TieredMenuPassThroughOptions;

const { t } = useTranslation();
const router = useRouter();
const { accounts, isSwitchingAccount, shouldRender, switchAccount, switchError } =
  useAccountSwitcher();

const selectedAccount = computed(
  () => accounts.value.find((account) => account.isSelected) ?? null,
);
const visibleAccounts = computed(() => accounts.value.slice(0, VISIBLE_ACCOUNT_LIMIT));
const hasOverflowAccounts = computed(() => accounts.value.length > VISIBLE_ACCOUNT_LIMIT);
const accountMenuItems = computed<AccountMenuItem[]>(() =>
  accounts.value.map((account) => {
    const menuItem: AccountMenuItem = {
      account,
      key: account.id,
      label: account.label,
      disabled: account.isSelected || isSwitchingAccount.value,
      command: () => {
        void switchAccount(account.id);
      },
    };

    return menuItem;
  }),
);
const accountOverflowMenuItems = computed<MenuItem[]>(() => [
  {
    key: ACCOUNT_MENU_TRIGGER_KEY,
    label: t('nav.view_all_profiles'),
    disabled: isSwitchingAccount.value,
    items: [
      {
        key: ACCOUNT_MENU_HEADER_KEY,
        label: t('nav.all_profiles'),
        disabled: true,
      },
      ...accountMenuItems.value,
      {
        key: ACCOUNT_MENU_FOOTER_SEPARATOR_KEY,
        separator: true,
      },
      {
        key: authRouteNames.createOrganization,
        label: t('nav.add_new_profile'),
        command: () => {
          void router.push({ name: authRouteNames.createOrganization });
        },
      },
    ],
  },
]);

const isAccountMenuItem = (item: MenuItem): item is AccountMenuItem => {
  return 'account' in item;
};

const isAccountMenuHeader = (item: MenuItem) => {
  return item.key === ACCOUNT_MENU_HEADER_KEY;
};

const isApplyNewAccountMenuItem = (item: MenuItem) => {
  return item.key === authRouteNames.createOrganization;
};

const goToSettings = () => {
  emit('close');
  void router.push({ name: 'settings' });
};

const isDataExportEnabled = useFeatureFlag(features.dataExport);

const goToDataExport = () => {
  emit('close');
  void router.push({ name: DATA_EXPORT_ROUTE_NAME });
};
</script>

<template>
  <section class="app-account-switcher" data-test="account-switcher" data-testid="account-switcher">
    <div
      v-if="selectedAccount"
      class="app-account-switcher__current"
      data-test="account-switcher-current"
      data-testid="account-switcher-current"
    >
      <span class="app-account-switcher__avatar" aria-hidden="true">
        {{ selectedAccount.initial }}
      </span>
      <span class="app-account-switcher__current-name">{{ selectedAccount.label }}</span>
    </div>

    <Button
      type="button"
      variant="text"
      plain
      class="app-account-switcher__account app-account-switcher__settings"
      data-test="account-switcher-settings"
      data-testid="account-switcher-settings"
      @click="goToSettings"
    >
      <span class="app-account-switcher__account-content">
        <span class="app-account-switcher__settings-icon pi pi-cog" aria-hidden="true" />
        <span class="app-account-switcher__account-label">{{ t('nav.settings') }}</span>
      </span>
    </Button>

    <Button
      v-if="isDataExportEnabled"
      type="button"
      variant="text"
      plain
      class="app-account-switcher__account app-account-switcher__settings"
      data-test="account-switcher-data-export"
      data-testid="account-switcher-data-export"
      @click="goToDataExport"
    >
      <span class="app-account-switcher__account-content">
        <span class="app-account-switcher__settings-icon pi pi-file" aria-hidden="true" />
        <span class="app-account-switcher__account-label">{{ t('nav.data_export') }}</span>
      </span>
    </Button>

    <div v-if="shouldRender" class="app-account-switcher__switch">
      <p class="app-account-switcher__title">{{ t('nav.switch_profiles') }}</p>

      <div class="app-account-switcher__list">
        <Button
          v-for="account in visibleAccounts"
          :key="account.id"
          type="button"
          variant="text"
          plain
          class="app-account-switcher__account app-account-switcher__item"
          :class="{ 'app-account-switcher__account--selected': account.isSelected }"
          :disabled="account.isSelected || isSwitchingAccount"
          :data-account-id="account.id"
          data-test="account-switcher-item"
          data-testid="account-switcher-item"
          @click="switchAccount(account.id)"
        >
          <span class="app-account-switcher__account-content">
            <span class="app-account-switcher__avatar" aria-hidden="true">
              {{ account.initial }}
            </span>
            <span class="app-account-switcher__account-label">{{ account.label }}</span>
          </span>

          <span
            v-if="account.isSelected"
            class="app-account-switcher__account-check pi pi-check"
            aria-hidden="true"
            data-test="account-switcher-check"
            data-testid="account-switcher-check"
          />
        </Button>

        <TieredMenu
          v-if="hasOverflowAccounts"
          id="account_switcher_accounts_menu"
          class="app-account-switcher__more-menu"
          :model="accountOverflowMenuItems"
          :pt="accountMenuPassThrough"
          unstyled
        >
          <template #item="{ item, label, props }">
            <span
              v-if="isAccountMenuHeader(item)"
              class="app-account-switcher__menu-heading"
              data-test="account-switcher-menu-title"
              data-testid="account-switcher-menu-title"
            >
              {{ label }}
            </span>

            <a
              v-else-if="isAccountMenuItem(item)"
              v-bind="props.action"
              class="app-account-switcher__account"
              data-test="account-switcher-menu-item"
              data-testid="account-switcher-menu-item"
              :data-account-id="item.account.id"
            >
              <span class="app-account-switcher__account-content">
                <span class="app-account-switcher__avatar" aria-hidden="true">
                  {{ item.account.initial }}
                </span>
                <span class="app-account-switcher__account-label">{{ item.account.label }}</span>
              </span>

              <span
                v-if="item.account.isSelected"
                class="app-account-switcher__account-check pi pi-check"
                aria-hidden="true"
                data-test="account-switcher-check"
                data-testid="account-switcher-check"
              />
            </a>
            <a
              v-else-if="isApplyNewAccountMenuItem(item)"
              v-bind="props.action"
              class="app-account-switcher__account app-account-switcher__apply-account"
              data-test="account-switcher-apply-new-account"
              data-testid="account-switcher-apply-new-account"
            >
              <span v-bind="props.label" class="app-account-switcher__account-label">
                {{ label }}
              </span>
            </a>
            <a
              v-else
              v-bind="props.action"
              class="app-account-switcher__account"
              data-test="account-switcher-more"
              data-testid="account-switcher-more"
            >
              <span class="app-account-switcher__account-content">
                <ListNarrowIcon class="app-account-switcher__menu-icon" aria-hidden="true" />
                <span v-bind="props.label" class="app-account-switcher__account-label">
                  {{ label }}
                </span>
              </span>
            </a>
          </template>
        </TieredMenu>
      </div>
    </div>

    <p v-if="switchError" class="app-account-switcher__error" role="alert">
      {{ t('nav.switch_profile_failed') }}
    </p>
  </section>
</template>

<style scoped lang="scss">
.app-account-switcher {
  display: flex;
  flex-direction: column;
  gap: $static-space-2;
  padding: $static-space-1 $static-space-1 $static-space-3;
  border-bottom: 1px solid var(--p-content-border-color);

  &__current {
    display: flex;
    align-items: center;
    gap: $static-space-2;
    padding: $static-space-3;
    border-radius: $radius-lg;
    background: var(--p-surface-100);
  }

  &__current-name {
    min-width: 0;
    overflow: hidden;
    color: var(--p-surface-700);
    font-size: $text-md;
    font-weight: $font-regular;
    line-height: $leading-tight;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__settings-icon {
    margin: 0 $static-space-1;
    color: var(--p-surface-400);
    font-size: $text-sm;
  }

  &__switch {
    display: flex;
    flex-direction: column;
    gap: $static-space-2;
    padding-top: $static-space-2;
    border-top: 1px solid var(--p-content-border-color);
  }

  &__title {
    margin: 0;
    padding: 0 $static-space-2;
    color: var(--p-surface-500);
    font-size: $text-xs;
    font-weight: $font-regular;
    line-height: $leading-tight;
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__account {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: calc($static-space-8 + $static-space-1);
    min-height: calc($static-space-8 + $static-space-1);
    gap: $static-space-3;
    padding: $static-space-2 $static-space-4 $static-space-2 $static-space-2;
    border: 0;
    border-radius: $radius-lg;
    color: var(--p-surface-900);
    background: transparent;
    box-sizing: border-box;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    &:not(:disabled):hover {
      background: var(--p-menu-item-focus-background);
    }

    &:disabled {
      cursor: default;
      opacity: 1;
    }
  }

  &__account-content {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    gap: $static-space-2;
  }

  &__avatar {
    display: inline-flex;
    flex: 0 0 $static-space-6;
    align-items: center;
    justify-content: center;
    width: $static-space-6;
    height: $static-space-6;
    overflow: hidden;
    border-radius: $radius-full;
    color: var(--p-surface-0);
    background: var(--p-surface-700);
    font-size: $text-sm;
    font-weight: $font-semibold;
    line-height: $leading-none;
  }

  &__account-label {
    min-width: 0;
    overflow: hidden;
    color: var(--p-surface-900);
    font-size: $text-sm;
    font-weight: $font-regular;
    line-height: $leading-tight;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__account-check {
    flex: 0 0 $static-space-4;
    color: #027777;
    font-size: $static-space-4;
  }

  &__menu-heading {
    display: block;
    color: var(--p-surface-500);
    font-size: $text-xs;
    font-weight: $font-semibold;
    line-height: $leading-tight;
    padding: $static-space-3 $static-space-3 $static-space-2 $static-space-2;
  }

  &__apply-account {
    margin: 0 $static-space-2 $static-space-1 0;
  }

  &__error {
    margin: 0;
    padding: 0 $static-space-2;
    color: var(--p-message-error-color);
    font-size: $text-xs;
    line-height: $leading-tight;
  }
}

.app-account-switcher__more-menu {
  width: 100%;
  min-width: 0;
  background: transparent;
}

:deep(.app-account-switcher__menu-list) {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

:deep(.app-account-switcher__menu-list--submenu) {
  position: absolute;
  inset-inline-start: auto !important;
  inset-inline-end: calc(100% + #{$static-space-3});
  z-index: 1;
  min-width: 100%;
  border: 1px solid var(--p-content-border-color);
  border-radius: $radius-lg;
  background: var(--p-surface-0);
  box-shadow: none;
  box-sizing: border-box;
  padding: 0 $static-space-1;
}

:deep(.app-account-switcher__menu-item) {
  position: relative;
}

:deep(.app-account-switcher__menu-item[data-p-disabled='true']) {
  pointer-events: none;
}

:deep(.app-account-switcher__menu-item[data-p-disabled='true'] .app-account-switcher__account) {
  cursor: default;
}

:deep(.app-account-switcher__menu-separator) {
  align-self: stretch;
  height: 1px;
  margin: $static-space-2 (-$static-space-1) $static-space-1;
  border: 0;
  background: var(--p-content-border-color);
}

:deep(.app-account-switcher__menu-icon) {
  flex: 0 0 $static-space-4;
  width: $static-space-4;
  height: $static-space-4;
  color: #027777;
  font-size: $static-space-4;
  margin-left: $static-space-1;
  margin-right: $static-space-1;
}

:deep(.app-account-switcher__menu-label) {
  min-width: 0;
}
</style>
